import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Button, Container } from 'react-bootstrap';
import ConsentSection from '../ConsentSection';
import './index.css';
import pinValidator from '../../../utils/pincodeValidator/index'; 

const FormFields = ({
 fields = [],
 onSubmit,
 isLoading,
 showConsentSection,
 onGoBack,
 initialValues = {},
 onConsentChange,
 hasConsent,
 onValidateLoanAndSSN  
}) => {
 const [formData, setFormData] = useState(initialValues);
 const [errors, setErrors] = useState({});
 const [touched, setTouched] = useState({});
 const [submitAttempted, setSubmitAttempted] = useState(false);
  // Star rating states
 const [statusUpdates, setStatusUpdates] = useState('');
 const [conditionsResolved, setConditionsResolved] = useState('');
 const [closingTime, setClosingTime] = useState('');
 const [experience, setExperience] = useState('');


// For the form component, use the same comparison logic:
const [isFeedbackPage, setIsFeedbackPage] = useState(false);
useEffect(() => {
  const currentPath = window.location.pathname;
  // Check if current path is exactly "/feedback" or "/feedback/"
  setIsFeedbackPage(currentPath === "/feedback" || currentPath === "/feedback/");
}, []);


 useEffect(() => {
   setFormData(prev => ({
     ...prev,
     ...initialValues
   }));
 }, [initialValues]);


 // Format phone number helper function
 const formatPhoneNumber = (phoneNumberString) => {
   const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
   const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
   if (match) {
     return '(' + match[1] + ') ' + match[2] + '-' + match[3];
   }
   return phoneNumberString;
 };


 const validateField = (name, value) => {
   const field = fields.find(f => f.name === name);
   if (!field || !field.validations) return null;


   // Use custom error message if provided
   if (field.required && (!value || (typeof value === 'string' && !value.trim()))) {
    // Add a null check for placeholder
    return field.customErrorMessage || 
      (field.placeholder ? `${field.placeholder.replace('*', '')} is required` : 'Please select an option');
  }

   if (field.validations.includes('email') && value) {
     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
     if (!emailRegex.test(value)) {
       return field.customErrorMessage || 'Please enter a valid email address';
     }
   }


   if (field.validations.includes('phone') && value) {
     // New phone validation logic
     const digits = value.replace(/\D/g, '');
    
     // Check for valid phone number format (10 digits)
     if (digits.length !== 10) {
       return field.customErrorMessage || 'Please enter a valid 10-digit phone number';
     }
    
     // Additional validation logic as needed
     // Check for invalid area codes, etc.
     const areaCode = digits.substring(0, 3);
     if (['000', '111', '123', '555', '999'].includes(areaCode)) {
       return 'Please enter a valid area code';
     }
   }


   if (field.validations.includes('zipcode') && value) {
     // First check if it's a valid format (5 digits)
     if (!/^\d{5}$/.test(value)) {
       return field.customErrorMessage || 'Please enter a valid 5-digit zipcode';
     }
    
     // Then check if the zipcode matches the selected state
     if (formData.State) {
       const stateFromZip = pinValidator(value);
       if (stateFromZip === 'none') {
         return 'Invalid zipcode';
       }
      
       // Find the state field
       const stateField = fields.find(f => f.name === 'State');
       if (stateField) {
         // Use formData.State to get the selected state
         const selectedStateOption = stateField.options.find(option => option.value === formData.State);
         const selectedStateName = selectedStateOption ? selectedStateOption.label : '';
        
         // Check if the zipcode corresponds to the selected state
         if (stateFromZip !== selectedStateName) {
           return 'Zipcode does not match selected state';
         }
       }
     }
   }

   // Enhanced file validation
   if (field.type === 'file') {
     if (field.required) {
       // Check if value is undefined, null, or an empty FileList
       if (!value || (value instanceof FileList && value.length === 0)) {
         return field.customErrorMessage || 'Please upload a file';
       }
     }
     
     // If we have a file, check file types if specified in accept
     if (value && value instanceof FileList && value.length > 0 && field.accept) {
       const allowedExtensions = field.accept.split(',').map(ext => ext.trim().toLowerCase());
       
       // Check each file against allowed extensions
       for (let i = 0; i < value.length; i++) {
         const fileName = value[i].name.toLowerCase();
         const fileExt = '.' + fileName.split('.').pop();
         
         if (!allowedExtensions.some(ext => 
           fileName.endsWith(ext) || ext === value[i].type || ext === '.*' || ext === '*.*'
         )) {
           return `Invalid file type. Allowed types: ${field.accept}`;
         }
       }
     }
   }

   // Special validation for document field
   if (field.type === 'document') {
     if (value === true && !formData.documentFiles) {
       return 'Please upload files for review';
     }
   }

   return null;
 };


 const validateForm = () => {
   setSubmitAttempted(true);
   const newErrors = {};
   let isValid = true;


   fields.forEach(field => {
     // Skip validation for label type fields
     if (field.type === 'label') return;
    
     const value = formData[field.name];
     const error = validateField(field.name, value);
     if (error) {
       newErrors[field.name] = error;
       isValid = false;
     }
   });

   // Check for file fields specifically
   const fileFields = fields.filter(field => field.type === 'file' && field.required);
   fileFields.forEach(field => {
     const value = formData[field.name];
     if (!value || (value instanceof FileList && value.length === 0)) {
       newErrors[field.name] = field.customErrorMessage || 'Please upload a file';
       isValid = false;
     }
   });

   // Check document field if documentSubmit is true
   if (formData.documentSubmit === true && (!formData.documentFiles || formData.documentFiles.length === 0)) {
     newErrors.documentFiles = 'Please upload files for review';
     isValid = false;
   }

   setErrors(newErrors);
   return isValid;
 };


 const handleChange = (e) => {
   const { name, value, type, files, checked } = e.target;
   let fieldValue;
   
   // Handle different input types
   if (type === 'file') {
     fieldValue = files;
   } else if (type === 'checkbox') {
     fieldValue = checked;
   } else if (type === 'radio') {
     // For document radio, we need to handle this specially
     if (name === 'documentSubmit') {
       fieldValue = value === 'yes';
       
       // If changing to "No", clear any document files
       if (value === 'no') {
         setFormData(prev => ({
           ...prev,
           documentFiles: null
         }));
       }
     } else {
       fieldValue = value;
     }
   } else {
     fieldValue = value;
   }
  
   // Format phone number input as user types
   if (name === 'Telephone' || name === 'PhoneNumber') {
     const digits = value.replace(/\D/g, '');
     if (digits.length <= 10) {
       // Apply formatting as they type
       if (digits.length === 0) {
         fieldValue = '';
       } else if (digits.length <= 3) {
         fieldValue = '(' + digits;
       } else if (digits.length <= 6) {
         fieldValue = '(' + digits.slice(0, 3) + ') ' + digits.slice(3);
       } else {
         fieldValue = '(' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6);
       }
     } else {
       // Keep the previous value if exceeding 10 digits
       fieldValue = formData[name] || '';
     }
   }
  
   setFormData(prev => ({
     ...prev,
     [name]: fieldValue
   }));


   if (touched[name] || submitAttempted) {
     const error = validateField(name, fieldValue);
     setErrors(prev => ({
       ...prev,
       [name]: error
     }));
   }
 };


 const handleBlur = (e) => {
   const { name, value, type, files } = e.target;
   setTouched(prev => ({
     ...prev,
     [name]: true
   }));
  
   // Format phone number on blur (to ensure consistent format)
   let fieldValue = type === 'file' ? files : value;
   if ((name === 'Telephone' || name === 'PhoneNumber') && value) {
     const digits = value.replace(/\D/g, '');
     if (digits.length === 10) {
       fieldValue = formatPhoneNumber(digits);
       // Update the formData with formatted value
       setFormData(prev => ({
         ...prev,
         [name]: fieldValue
       }));
     }
   }
  // Trigger loan and SSN validation when both fields have values and user leaves either field
  if ((name === 'Loan_Number' || name === 'SSN') && 
  formData.Loan_Number && formData.Loan_Number.length === 12 && 
  formData.SSN && formData.SSN.length === 4) {
// Call a callback function passed from parent to validate loan and SSN
if (onValidateLoanAndSSN) {
  onValidateLoanAndSSN(formData.Loan_Number, formData.SSN);
}
}

   const error = validateField(name, fieldValue);
   setErrors(prev => ({
     ...prev,
     [name]: error
   }));
 };


 // File upload handler for document field
 const handleFileUpload = (e) => {
   const files = e.target.files;
   let fileArray = [];
   
   for (let i = 0; i < files.length; i++) {
     fileArray.push(files[i]);
   }
   
   setFormData(prev => ({
     ...prev,
     documentFiles: fileArray
   }));
   
   // Clear error if files were uploaded
   if (fileArray.length > 0) {
     setErrors(prev => ({
       ...prev,
       documentFiles: null
     }));
   }
 };


 const handleSubmit = (e) => {
   e.preventDefault();
  
   if (showConsentSection) {
     // Don't validate fields for consent section
     onSubmit();
     return;
   }

   // Validate the entire form
   if (validateForm()) {
     // If it's the feedback page, add the rating data to the form data
     if (isFeedbackPage) {
       const formDataWithRatings = {
         ...formData,
         StatusUpdates: statusUpdates,
         ConditionsResolved: conditionsResolved,
         ClosingTime: closingTime,
         Experience: experience
       };
       onSubmit(formDataWithRatings);
     } else {
       onSubmit(formData);
     }
   } else {
     // Scroll to first error
     const firstErrorField = document.querySelector('.is-invalid');
     if (firstErrorField) {
       firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
     }
   }
 };


 // Star rating change handlers
 const handleStatusUpdateChange = (value) => {
   setStatusUpdates(value === statusUpdates ? '' : value);
 };


 const handleConditionsResolved = (value) => {
   setConditionsResolved(value === conditionsResolved ? '' : value);
 };


 const handleClosingTimeChange = (value) => {
   setClosingTime(value === closingTime ? '' : value);
 };


 const handleExperienceChange = (value) => {
   setExperience(value === experience ? '' : value);
 };


 const renderField = (field) => {
   const {
     type = 'text',
     name,
     label,
     required,
     options = [],
     placeholder,
     disabled = false,
     accept,
     ...rest
   } = field;


   const hasError = (touched[name] || submitAttempted) && errors[name];


   const commonProps = {
     name,
     onChange: handleChange,
     onBlur: handleBlur,
     isInvalid: hasError,
     disabled,
     placeholder,
     ...rest
   };


   if (type !== 'file' && type !== 'label' && type !== 'document') {
     commonProps.value = formData[name] || '';
   }


   switch (type) {
     case 'label':
       return (
         <Form.Group className="mb-3">
           <Form.Label className="form-label fw-bold">
             {placeholder}
           </Form.Label>
         </Form.Group>
       );
      
     case 'select':
       return (
         <Form.Group className="mb-3">
           <Form.Select {...commonProps}>
             <option value="">{placeholder || "Select an option"}</option>
             {options.map((option) => (
               <option key={option.value} value={option.value}>
                 {option.label}
               </option>
             ))}
           </Form.Select>
           <Form.Control.Feedback type="invalid">
             {errors[name]}
           </Form.Control.Feedback>
         </Form.Group>
       );


     case 'radio':
       return (
         <Form.Group className="mb-3">
           <div>
             {options.map((option) => (
               <Form.Check
                 key={`${name}-${option.value}`}
                 inline
                 type="radio"
                 id={`${name}-${option.value}`}
                 name={name}
                 value={option.value}
                 label={option.label}
                 checked={formData[name] === option.value}
                 onChange={handleChange}
                 isInvalid={hasError}
               />
             ))}
           </div>
           {hasError && (
             <div className="invalid-feedback d-block">
               {errors[name]}
             </div>
           )}
         </Form.Group>
       );
     
     case 'document':
       return (
         <Form.Group className="mb-3">
           <label>{placeholder || "Do you have any documents to submit for review?"}</label>
           <div className="mt-2">
             <label className="me-3">
               <input 
                 type="radio" 
                 name="documentSubmit"
                 value="yes"
                 onChange={handleChange}
                 checked={formData.documentSubmit === true}
                 className="me-1"
               />
               Yes
             </label>
             <label>
               <input 
                 type="radio" 
                 name="documentSubmit" 
                 value="no"
                 onChange={handleChange}
                 checked={formData.documentSubmit === false}
                 defaultChecked={formData.documentSubmit === undefined}
                 className="me-1"
               />
               No
             </label>
           </div>
           
           {formData.documentSubmit === true && (
             <div className="mt-3">
               <label style={{ fontWeight: '600' }}>
                 File Upload* <span style={{ fontWeight: '300' }}>(Press Ctrl to select multiple files)</span>
               </label>
               <input 
                 type="file"
                 className={`form-control mt-2 ${errors.documentFiles ? 'is-invalid' : ''}`}
                 onChange={handleFileUpload}
                 multiple
                 accept={accept || ".doc, .docx, .pdf"}
               />
               {errors.documentFiles && (
                 <div className="invalid-feedback d-block">
                   {errors.documentFiles}
                 </div>
               )}
             </div>
           )}
         </Form.Group>
       );

     case 'textarea':
       return (
         <Form.Group className="mb-3">
           <Form.Control
             as="textarea"
             rows={4}
             {...commonProps}
           />
           <Form.Control.Feedback type="invalid">
             {errors[name]}
           </Form.Control.Feedback>
         </Form.Group>
       );


     case 'file':
       return (
         <Form.Group className="mb-3">
           <Form.Label>
             {placeholder}
             {required && <span className="text-danger"> *</span>}
           </Form.Label>
           <Form.Control
             type="file"
             {...commonProps}
             accept={accept}
             required={required}
           />
           <Form.Control.Feedback type="invalid">
             {errors[name]}
           </Form.Control.Feedback>
         </Form.Group>
       );


     case 'phone':
       return (
         <Form.Group className="mb-3">
           <Form.Control
             type="tel"
             {...commonProps}
             // Apply a mask to help with correct phone number format
             placeholder={placeholder || "(___) ___-____"}
             maxLength={14}
           />
           <Form.Control.Feedback type="invalid">
             {errors[name]}
           </Form.Control.Feedback>
         </Form.Group>
       );


     default:
       return (
         <Form.Group className="mb-3">
           <Form.Control
             type={type}
             {...commonProps}
           />
           <Form.Control.Feedback type="invalid">
             {errors[name]}
           </Form.Control.Feedback>
         </Form.Group>
       );
   }
 };


 // Render star rating section this is only for /feedback page
 const renderRatingSection = () => {
   if (!isFeedbackPage) return null;
  
   return (
     <div className="rating-section mb-4">
       <div className="customDesc">
         <p className="rating-header">
           If you have recently closed a loan with us, we would like you to
           rate us on below parameters:
         </p>
       </div>


       <Row>
         <Col md={7}>
           <label className="customLabel">
             Proactiveness of our representative in keeping you updated on
             loan status:
           </label>
         </Col>
         <Col md={5}>
           <div className="userWrapper">
             <div className="formRating">
               {[1, 2, 3, 4, 5].map((rating) => (
                 <span
                   key={rating}
                   className={`star-rating ${Number(statusUpdates) >= rating ? 'active' : ''}`}
                   onClick={() => handleStatusUpdateChange(rating.toString())}
                   aria-label={`Rating ${rating}`}
                 >
                  ★
                 </span>
               ))}
             </div>
           </div>
         </Col>
       </Row>


       <Row>
         <Col md={7}>
           <label className="customLabel">
             Proactiveness of our representative in getting the conditions
             resolved:
           </label>
         </Col>
         <Col md={5}>
           <div className="userWrapper">
             <div className="formRating">
               {[1, 2, 3, 4, 5].map((rating) => (
                 <span
                   key={rating}
                   className={`star-rating ${Number(conditionsResolved) >= rating ? 'active' : ''}`}
                   onClick={() => handleConditionsResolved(rating.toString())}
                   aria-label={`Rating ${rating}`}
                 >
                  ★
                 </span>
               ))}
             </div>
           </div>
         </Col>
       </Row>


       <Row>
         <Col md={7}>
           <label className="customLabel">
             Overall time taken on loan closing:
           </label>
         </Col>
         <Col md={5}>
           <div className="userWrapper">
             <div className="formRating">
               {[1, 2, 3, 4, 5].map((rating) => (
                 <span
                   key={rating}
                   className={`star-rating ${Number(closingTime) >= rating ? 'active' : ''}`}
                   onClick={() => handleClosingTimeChange(rating.toString())}
                   aria-label={`Rating ${rating}`}
                 >
                  ★
                 </span>
               ))}
             </div>
           </div>
         </Col>
       </Row>


       <Row>
         <Col md={7}>
           <label className="customLabel">
             Your overall experience in doing business with us:
           </label>
         </Col>
         <Col md={5}>
           <div className="userWrapper">
             <div className="formRating">
               {[1, 2, 3, 4, 5].map((rating) => (
                 <span
                   key={rating}
                   className={`star-rating ${Number(experience) >= rating ? 'active' : ''}`}
                   onClick={() => handleExperienceChange(rating.toString())}
                   aria-label={`Rating ${rating}`}
                 >
                  ★
                 </span>
               ))}
             </div>
           </div>
         </Col>
       </Row>
     </div>
   );
 };


 if (showConsentSection) {
   return (
     <Container className="consent-buttons-container">
       <Row className="justify-content-center">
         <Col xs={12} className="text-center consent-buttons-columns">
           <Button
             variant="secondary"
             onClick={onGoBack}
             className="me-3 "
           >
              &lt; Back
           </Button>
           <Button
             variant="primary"
             onClick={handleSubmit}
             disabled={isLoading}
           >
             {isLoading ? 'Submitting...' : 'Continue'}
           </Button>
         </Col>
       </Row>
     </Container>
   );
 }


 return (
   <Form onSubmit={handleSubmit} className="form-container-component">
     <Row>
       {fields.map((field) => (
         <Col key={field.name} {...field.colProps}>
           {renderField(field)}
         </Col>
       ))}
     </Row>
    
     {/* Star Rating Section */}
     {renderRatingSection()}
    
     {/* Add ConsentSection here */}
     <div className="mb-4">
       <ConsentSection
         onConsentChange={onConsentChange}
         hasConsent={hasConsent}
         showClickToMoreOpt={false}
       />
     </div>
    
     <div className="text-center mt-4">
       <Button
         type="submit"
         variant="primary"
         className="form-submit-btn"
         disabled={isLoading}
       >
         {isLoading ? 'Please Wait...' : 'Submit'}
       </Button>
     </div>
   </Form>
 );
};


export default FormFields;